import axiosInstance from '../axios';
import { API_ROUTES } from '../../enums/API_ROUTES';
import { getTokenFromLocalStorage } from '../../helpers/localStorage';

export type S3StorageAuditClassification = 'TRACKED' | 'UNLINKED_VIDEO' | 'NO_DB_RECORD' | 'ARCHIVED_BUT_PRESENT' | 'SHARED' | 'UNKNOWN';

export type S3StorageAuditSummary = {
  status: string;
  displayed_storage_bytes: number;
  actual_storage_bytes: number | null;
  difference_bytes: number | null;
  difference_percent: number | null;
  suspected_orphans_count: number;
  suspected_orphans_bytes: number;
  archived_but_present_count: number;
  archived_but_present_bytes: number;
  object_count: number;
  asset_count: number;
  last_scanned_at: string | null;
  scan_requested_at: string | null;
  scan_started_at: string | null;
  scan_error: string | null;
  actual_storage_scope?: 'CURRENT_SERVER_PREFIX' | 'CURRENT_AND_LEGACY_ORG_SCOPES';
  displayed_storage_scope?: 'ORGANIZATION_STORAGE_USAGE';
  orphan_scan?: OrphanScanSummary | null;
};

export type SharedS3ReferenceScan = {
  configured: boolean;
  round_id: string | null;
  complete: boolean;
  deletion_safe: boolean;
  error: string | null;
  servers_total: number;
  servers_scanned: number;
  snapshot_token?: string | null;
  servers: Array<{
    server_id: string;
    server_prefix: string;
    registered: boolean;
    scanned: boolean;
    scanned_at: string | null;
  }>;
};

export type OrphanScanStatus = 'NOT_RUN' | 'NOT_SCANNED' | 'PENDING' | 'SCANNING' | 'COMPLETED' | 'FAILED';

export type OrphanScanSummary = {
  policy_version?: number;
  status: OrphanScanStatus;
  requested_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  source_scan_at: string | null;
  generated_at?: string | null;
  error: string | null;
  stale: boolean;
  summary: {
    candidates_checked: number;
    safe_candidates: { count: number; size_bytes: number };
    referenced: { count: number; size_bytes: number };
    needs_review: { count: number; size_bytes: number };
  } | null;
  shared_reference_scan?: SharedS3ReferenceScan | null;
  warnings?: string[];
};

export type ServerS3StorageSummary = {
  server_prefix: string;
  status: string;
  displayed_storage_bytes: number;
  total_size_bytes: number | null;
  current_prefix_size_bytes?: number | null;
  actual_scope?: 'CURRENT_SERVER_PREFIX' | 'ALL_BUCKET_PREFIXES';
  prefix_breakdown?: Array<{
    prefix: string;
    total_size_bytes: number;
    object_count: number;
  }> | null;
  object_count: number;
  scan_requested_at: string | null;
  scan_started_at: string | null;
  last_scanned_at: string | null;
  scan_error: string | null;
  shared_reference_scan?: SharedS3ReferenceScan;
};

export type S3StorageAuditBreakdownItem = {
  key: string;
  label?: string;
  count: number;
  size_bytes: number;
};

export type S3StorageAuditVideofeed = {
  id: number;
  title: string;
};

export type S3StorageAuditAsset = {
  id: number;
  name: string;
  s3_prefix: string;
  storage_scope?: 'CURRENT_SERVER' | 'DB_CONFIRMED_LEGACY' | 'LEGACY';
  total_size_bytes: number;
  source_size_bytes: number;
  hls_size_bytes: number;
  thumbnail_size_bytes: number;
  metadata_size_bytes: number;
  other_size_bytes: number;
  object_count: number;
  last_modified_at: string | null;
  video_ids: number[];
  videofeeds: S3StorageAuditVideofeed[];
  db_statuses: string[];
  classification: S3StorageAuditClassification;
  classification_reason: string;
  orphan_verdict?: OrphanReconciliationVerdict | null;
  reference_count: number;
};

export type S3StorageAuditObject = {
  key: string;
  size_bytes: number;
  file_type: string;
  last_modified_at: string | null;
};

export type S3StorageAuditReference = {
  type: string;
  id: string | number;
  title?: string;
  status?: string;
  organization_id?: number;
};

export type S3StorageAuditAssetDetails = S3StorageAuditAsset & {
  objects: S3StorageAuditObject[];
  references?: S3StorageAuditReference[];
  uploaded_by?: string | null;
  uploaded_at?: string | null;
};

export type S3StorageAuditVideofeedGroup = {
  videofeed_id: number | null;
  title: string;
  type: string | null;
  status: string | null;
  is_reference_timeline?: boolean;
  total_size_bytes: number;
  source_size_bytes: number;
  hls_size_bytes: number;
  thumbnail_size_bytes: number;
  metadata_size_bytes: number;
  other_size_bytes: number;
  object_count: number;
  asset_count: number;
  video_ids: number[];
  classifications: S3StorageAuditClassification[];
  shared_asset_count: number;
  reference_count: number;
  last_modified_at: string | null;
  assets: S3StorageAuditAsset[];
};

export type S3StorageAuditFilters = {
  page: number;
  limit: number;
  search?: string;
  classification?: string;
  fileType?: string;
  dbStatus?: string;
  dateFrom?: string;
  dateTo?: string;
  minSize?: number;
  sort?: string;
  orphansOnly?: boolean;
  groupBy?: 'videofeed';
};

export type S3StorageAuditResponse = {
  summary: S3StorageAuditSummary;
  breakdown: {
    file_types: S3StorageAuditBreakdownItem[];
    classifications: S3StorageAuditBreakdownItem[];
    disparity: S3StorageAuditBreakdownItem[];
  };
  documents: S3StorageAuditAsset[];
  videofeeds: S3StorageAuditVideofeedGroup[];
  total: number;
  page: number;
  limit: number;
};

export type OrphanReconciliationVerdict = 'SAFE_CANDIDATE' | 'REFERENCED' | 'NEEDS_REVIEW';

export type OrphanReconciliationEvidence = {
  type: string;
  id: string | number;
  field?: string;
  status?: string;
  organization_id?: number;
  description: string;
  protects_asset: boolean;
};

export type OrphanReconciliationCandidate = {
  asset_id: number;
  name: string;
  s3_prefix: string;
  classification: S3StorageAuditClassification;
  verdict: OrphanReconciliationVerdict;
  reasons: string[];
  total_size_bytes: number;
  object_count: number;
  last_modified_at: string | null;
  video_rows: Array<{
    id: number;
    status: string | null;
    is_additional_angle: boolean;
  }>;
  evidence: OrphanReconciliationEvidence[];
};

export type OrphanReconciliationReport = {
  mode: 'DRY_RUN';
  policy_version?: number;
  deletion_enabled: false;
  objects_deleted: 0;
  organization_id: number;
  generated_at: string;
  source_scan_at: string;
  shared_reference_scan?: SharedS3ReferenceScan;
  checked_database_tables: string[];
  summary: {
    candidates_checked: number;
    safe_candidates: { count: number; size_bytes: number };
    referenced: { count: number; size_bytes: number };
    needs_review: { count: number; size_bytes: number };
  };
  warnings: string[];
  candidates: OrphanReconciliationCandidate[];
};

export type OrphanDeletion = {
  id: number;
  organization_id: number;
  operation?: 'DELETE' | 'RESTORE';
  status: 'PENDING' | 'PROCESSING' | 'ACTIVE' | 'PARTIAL' | 'FAILED' | 'RESTORED';
  assets: Array<{
    asset_id: number;
    name: string;
    s3_prefix: string;
    total_size_bytes: number;
    object_count: number;
  }>;
  asset_count: number;
  object_count: number;
  total_size_bytes: number;
  deleted_at: string;
  restore_until: string;
  restored_at: string | null;
  processed_object_count?: number;
  progress_percent?: number;
  completed_at?: string | null;
  progress_message?: string | null;
  error: string | null;
  can_restore: boolean;
};

const getAuditRoute = (organizationId: string) => `${API_ROUTES.ORGANIZATION}${organizationId}/s3-storage-audit`;
const serverAuditRoute = `${API_ROUTES.ORGANIZATION}s3-storage-audit`;

export const getServerS3StorageSummary = async () => {
  return axiosInstance.get<ServerS3StorageSummary>(`${serverAuditRoute}/server-summary`);
};

export const startServerS3StorageScan = async () => {
  return axiosInstance.post<ServerS3StorageSummary>(`${serverAuditRoute}/server-scan`);
};

export const getS3StorageAuditSummary = async (organizationId: string) => {
  return axiosInstance.get<S3StorageAuditSummary>(`${getAuditRoute(organizationId)}/summary`);
};

export const getS3StorageAudit = async (organizationId: string, filters: S3StorageAuditFilters) => {
  return axiosInstance.get<S3StorageAuditResponse>(getAuditRoute(organizationId), { params: filters });
};

export const getS3StorageAuditAsset = async (organizationId: string, assetId: number) => {
  return axiosInstance.get<S3StorageAuditAssetDetails>(`${getAuditRoute(organizationId)}/assets/${assetId}`);
};

export const startS3StorageAudit = async (organizationId: string) => {
  return axiosInstance.post<{ status: string; scan_requested_at: string }>(`${getAuditRoute(organizationId)}/scan`);
};

export const startOrphanFileScan = async (organizationId: string) => {
  return axiosInstance.post<{
    status: OrphanScanStatus;
    requested_at: string;
    s3_status: string;
    s3_requested_at: string;
    s3_reused: boolean;
  }>(`${getAuditRoute(organizationId)}/orphans/scan`);
};

export const runOrphanReconciliationDryRun = async (organizationId: string) => {
  return axiosInstance.post<OrphanReconciliationReport>(`${getAuditRoute(organizationId)}/reconcile-dry-run`);
};

export const deleteOrphanFiles = async (organizationId: string, orphanScanCompletedAt: string) => {
  return axiosInstance.post<OrphanDeletion>(`${getAuditRoute(organizationId)}/v3/orphans/delete`, {
    delete_all_verified: true,
    orphan_scan_completed_at: orphanScanCompletedAt,
  });
};

export const getOrphanDeletions = async (organizationId: string) => {
  return axiosInstance.get<OrphanDeletion[]>(`${getAuditRoute(organizationId)}/orphans/deletions`);
};

export const cancelOrphanDeletion = async (
  organizationId: string,
  deletionId: number,
) => {
  return axiosInstance.post<OrphanDeletion>(
    `${getAuditRoute(organizationId)}/orphans/deletions/${deletionId}/cancel`,
  );
};

export const restoreOrphanDeletion = async (organizationId: string, deletionId: number) => {
  return axiosInstance.post<OrphanDeletion>(`${getAuditRoute(organizationId)}/orphans/deletions/${deletionId}/restore`);
};

export const exportS3StorageAudit = async (organizationId: string, filters: Omit<S3StorageAuditFilters, 'page' | 'limit'>) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  });
  const query = params.toString();

  const response = await fetch(`${process.env.REACT_APP_API_URL}${getAuditRoute(organizationId)}/export${query ? `?${query}` : ''}`, {
    headers: {
      Authorization: `Bearer ${getTokenFromLocalStorage()}`,
      Accept: 'text/csv',
    },
  });

  if (!response.ok) throw new Error((await response.text()) || `CSV export failed (${response.status})`);
  return response.blob();
};
